/* eslint-disable no-plusplus */
/* eslint-disable no-unused-vars */
/* eslint-disable immutable/no-mutation */
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { productsApi } from "../../services/products";
import { ProductItem } from "../ProductItem/ProductItem";
import { NoSearchResultFound } from "../NoSearchResultFound/NoSearchResultFound";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  setParsedFilters,
  setAppliedFilters,
} from "../../features/products/productsSlice";
import "./ProductList.scss";
import { Container } from "../Layout/Container";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { NavigationButtons } from "./NavigationButtons/NavigationButtons";
import { TagSection } from "./TagSection/TagSection";

const PRODUCTS_PER_PAGE = 16;

/**
 * Maps internal tag keys to API slug paths:
 *   bestseller  -> bestseller
 *   new         -> new_arrivals
 *   exclusive   -> exclusive
 *   limited     -> limited
 *   others      -> others   (uncategorized / no-tag products)
 */
const TAG_SECTIONS = [
  { key: "bestseller", slug: "bestseller", translationKey: "sections.bestseller", priority: 1 },
  { key: "new",        slug: "new_arrivals", translationKey: "sections.new",       priority: 2 },
  { key: "exclusive",  slug: "exclusive",    translationKey: "sections.exclusive",  priority: 3 },
  { key: "limited",    slug: "limited",      translationKey: "sections.limited",    priority: 4 },
  { key: "others",     slug: "others",       translationKey: "sections.others",     priority: 5 },
];

export const ProductsList = () => {
  const { t, i18n } = useTranslation("application");
  const { lang } = useParams();
  const isRTL = i18n.language === "ar";
  const dispatch = useDispatch();

  const appliedFilters = useSelector((state) => state.products.appliedFilters);
  const activeFilters = useSelector((state) => state.products.activeFilters);
  const mainCategory = useSelector((state) => state.products.mainCategory);

  const [isAppliedFiltersReady, setIsAppliedFiltersReady] = useState(false);
  const sectionRefs = useRef([]);

  const parseActiveFilters = (filters, category) => {
    const allFilters = [];
    Object.keys(filters).forEach((filter) => {
      if (filters[filter].length !== 0) {
        if (filter === "searchPhrase") {
          allFilters.push({ name: "filters[q]", value: filters[filter] });
          return;
        }
        allFilters.push(...filters[filter]);
      }
    });
    if (mainCategory === "Coffee") {
      allFilters.push({ name: "filters[grouping]", value: "coffee" });
    } else if (category === "Tools") {
      allFilters.push({ name: "filters[grouping]", value: "equipment" });
    } else if (category === "Archive") {
      allFilters.push({ name: "filters[archived]", value: true });
    } else if (category === "SpareParts") {
      allFilters.push({ name: "filters[grouping]", value: "spare_part" });
    }
    return allFilters;
  };

  useEffect(() => {
    const parsedFilters = parseActiveFilters(activeFilters, mainCategory);
    dispatch(setAppliedFilters([...parsedFilters]));
    setIsAppliedFiltersReady(true);
  }, [activeFilters, mainCategory]);

  // Build the translated section list
  const sections = useMemo(
    () =>
      TAG_SECTIONS.map((s) => ({
        ...s,
        title: t(s.translationKey),
      })),
    [i18n.language]
  );

  if (!isAppliedFiltersReady) {
    return (
      <Container className="single-product-container">
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div key="product-sections">
        <div className="product-sections">
          {sections.map((section, index) => (
            <TagSection
              key={section.key}
              tagSlug={section.slug}
              title={section.title}
              appliedFilters={appliedFilters}
              sectionIndex={index}
              sectionRefs={sectionRefs}
            />
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
